import React, { useState } from 'react';
import Labrador from '../components/Labrador';
import HelpModal from '../components/HelpModal';

/**
 * Pre-login landing page. Shown to signed-out visitors before AuthPage;
 * `onEnter(mode)` switches AuthGate to the auth view ('login' | 'register').
 */
const FEATURES = [
  {
    title: 'Envelope budgeting',
    body: 'Every dollar that arrives is Up for Grabs — grab it into envelopes until it all has a job. Available = Grabbed − Spent, always.',
  },
  {
    title: 'A strategy, not just a ledger',
    body: 'Set savings, debt-payoff, and spending goals. When goals exceed income, cuts cascade sensibly — variable spending first, fixed costs never.',
  },
  {
    title: 'The whole household',
    body: 'Members, incomes, 401ks, IRAs, debts, and spending goals in one place — with net worth and an emergency-fund total that update live.',
  },
];

export default function LandingPage({ onEnter }) {
  const [showHelp, setShowHelp] = useState(false);
  const [showAbout, setShowAbout] = useState(false);

  const navBtn = {
    background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer',
    fontSize: 13, fontWeight: 600, padding: '6px 10px',
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Nav */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 24px', flexWrap: 'wrap', gap: 10,
      }}>
        <div className="brand" style={{ fontSize: 16 }}>
          <Labrador size={22} className="brand-mark" />
          <span>TRIGGER BUDGET</span>
          <span className="beta-badge">BETA</span>
        </div>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          <button style={navBtn} onClick={() => setShowAbout(true)}>About us</button>
          <button style={navBtn} onClick={() => setShowHelp(true)}>Help</button>
          <button style={navBtn} onClick={() => onEnter('login')}>Sign in</button>
          <button className="btn btn-primary btn-sm" style={{ marginLeft: 6 }}
            onClick={() => onEnter('register')}>
            Get started
          </button>
        </nav>
      </header>

      {/* Hero */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', textAlign: 'center', padding: '40px 24px' }}>
        <Labrador size={64} style={{ color: 'var(--blue)', marginBottom: 18,
          filter: 'drop-shadow(0 0 18px rgba(102,153,242,0.45))' }} />
        <h1 style={{ fontSize: 'clamp(28px, 6vw, 44px)', fontWeight: 900, letterSpacing: '0.18em',
          marginBottom: 10 }}>
          TRIGGER BUDGET
        </h1>
        <div style={{ fontSize: 14, letterSpacing: '0.25em', textTransform: 'uppercase',
          color: 'var(--text2)', marginBottom: 14 }}>
          Run Wild. Spend Smart.
        </div>
        <p style={{ fontSize: 15, color: 'var(--text2)', maxWidth: 480, lineHeight: 1.6, marginBottom: 26 }}>
          Plan, track, and trigger your family budget.
        </p>
        <div style={{ display: 'flex', gap: 10, marginBottom: 34, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button className="btn btn-primary" style={{ padding: '10px 26px', fontSize: 14 }}
            onClick={() => onEnter('register')}>
            Create your budget
          </button>
          <button className="btn" style={{ padding: '10px 26px', fontSize: 14 }}
            onClick={() => onEnter('login')}>
            Sign in
          </button>
        </div>

        {/* Privacy promise */}
        <div style={{
          maxWidth: 560, background: 'var(--green-bg)', border: '1px solid var(--green)',
          borderRadius: 12, padding: '14px 18px', fontSize: 13, color: 'var(--green)',
          lineHeight: 1.6, marginBottom: 40,
        }}>
          <strong>No logins. No syncing. No exceptions.</strong> Your bank credentials never touch
          Trigger Budget, because we never ask for them. We wouldn't link our own accounts to an
          app — we're not about to ask you to.
        </div>

        {/* Features */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 14, maxWidth: 860, width: '100%', textAlign: 'left' }}>
          {FEATURES.map(f => (
            <div key={f.title} className="card" style={{ margin: 0, padding: '18px 20px' }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>{f.title}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.6 }}>{f.body}</div>
            </div>
          ))}
        </div>
      </main>

      <footer style={{ textAlign: 'center', padding: '18px 24px', fontSize: 11, color: 'var(--text3)' }}>
        Trigger Budget · in beta — feedback from inside the app shapes what gets built next.
      </footer>

      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}

      {showAbout && (
        <AboutModal onClose={() => setShowAbout(false)} onEnter={onEnter} />
      )}
    </div>
  );
}

function AboutModal({ onClose, onEnter }) {
  // Lightweight inline modal (same shell classes as Modal.js) so About stays
  // one self-contained page component.
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-header">
          <h3>About Trigger Budget</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body help-body" style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>
          <p>
            Trigger Budget started as one family's spreadsheet — a way to give every dollar a job,
            keep fixed costs honest, and know exactly what happens to the plan when life demands a
            cut. It grew into the app you're looking at.
          </p>
          <p>
            It's built around a simple belief: a budget should be something you <em>run</em>, not
            something that runs you. Enter what's real, set goals worth chasing, and let the
            strategy math show the trade-offs.
          </p>
          <p>
            We're in beta, and feedback from real households drives the roadmap — there's a
            Feedback button inside the app, and we read everything.
          </p>
          <button className="btn btn-primary" style={{ width: '100%', marginTop: 14 }}
            onClick={() => { onClose(); onEnter('register'); }}>
            Join the beta
          </button>
        </div>
      </div>
    </div>
  );
}
